import { Button } from "../../../../components/common/Button";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { savedPayoutV2Data } from "../../redux/formDataSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { ContentWrapper, Error, ItemWrapper } from "../styles";
import CustomSelect from "../../../../components/common/CustomSelect";
import { GHS_PAYMENT_DESCRIPTION } from "../../const";
import { Input } from "../../../../components/common/Input";
import { fetchBankList } from "../../redux/slice";
import { capitalizeCurrencyCode } from "../../../../utils/helpers";
import { useParams } from "react-router-dom";

type GHSBeneficiaryTypes = {
  handleNext?: () => void;
  handlePrevious?: () => void;
  paymentMethod?: string;
  paymentMethodName?: string;
  bankName?: string;
  accountNumber?: string;
  accountName?: string;
  paymentDescription?: string;
  banks?: string[];
  senderIdNumber?: string;
};

const Beneficiary = ({ handleNext, handlePrevious }: GHSBeneficiaryTypes) => {
  const dispatch = useAppDispatch();
  const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const [userData, setUserData] = useState<GHSBeneficiaryTypes>();
  const { currencyCode } = useParams();

  const PAYMENT_METHOD = getSavedData?.paymentMethod?.includes("BANK_ACCOUNT");

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<GHSBeneficiaryTypes>();

  const publicKey = keys?.data?.publicKey;

  useEffect(() => {
    if (getSavedData) {
      Object.keys(getSavedData).forEach((key) => {
        setValue(
          key as keyof GHSBeneficiaryTypes,
          getSavedData[key as keyof GHSBeneficiaryTypes],
        );
      });
    }
  }, [getSavedData, setValue]);

  const handleDataUpdate = (field: keyof GHSBeneficiaryTypes, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    const fetchBanks = async () => {
      const action = await dispatch(
        fetchBankList({
          xAuthToken: publicKey,
          country: capitalizeCurrencyCode(currencyCode),
          type: !PAYMENT_METHOD ? "mobile-money" : undefined,
        }),
      );

      if (fetchBankList.fulfilled.match(action)) {
        const uniqueBanks = Array.from(
          new Map(
            action.payload.data.map((bank: any) => [bank.code, bank]),
          ).values(),
        );

        handleDataUpdate("banks", uniqueBanks);
      }
    };

    fetchBanks().then((r) => r);
  }, [businessId]);

  const bankOptions = useMemo(
    () =>
      userData?.banks?.map((bank: any) => ({
        value: bank.code,
        label: bank.name,
      })),
    [userData?.banks],
  );

  const onSubmit: SubmitHandler<GHSBeneficiaryTypes> = useCallback(
    (data) => {
      const selectedBank = bankOptions?.find(
        (bank) => bank.value === data.bankName,
      );

      const combinedData = {
        ...userData,
        ...data,
        bankName: selectedBank?.label,
        bankCode: selectedBank?.value,
      };

      dispatch(savedPayoutV2Data(combinedData));

      handleNext?.();
    },
    [dispatch, userData, bankOptions],
  );

  return (
    <ContentWrapper>
      <div>
        <Controller
          name="paymentMethodName"
          control={control}
          rules={{ required: false }}
          render={({ field }) => {
            return (
              <CustomSelect
                {...field}
                $width="100%"
                defaultValue={getSavedData?.paymentMethodName}
                placeholder="Select payment method"
                disabled
              />
            );
          }}
        />
      </div>

      <div>
        <Controller
          name="bankName"
          control={control}
          rules={{ required: "Bank name is required" }}
          render={({ field }) => {
            return (
              <CustomSelect
                {...field}
                placeholder={"Bank name"}
                $width={"100%"}
                options={bankOptions}
                showSearch
              />
            );
          }}
        />
        {<Error>{errors?.bankName?.message}</Error>}
      </div>

      {PAYMENT_METHOD && (
        <div>
          <Controller
            name="accountNumber"
            control={control}
            rules={{ required: "Account number is required" }}
            render={({ field }) => {
              return (
                <Input
                  label={"Account number"}
                  value={field.value}
                  onChange={field.onChange}
                />
              );
            }}
          />
          {<Error>{errors?.accountNumber?.message}</Error>}
        </div>
      )}

      <div>
        <Controller
          name="accountName"
          control={control}
          rules={{ required: "Account name is required" }}
          render={({ field }) => {
            return (
              <Input
                label={"Account name"}
                value={field.value}
                onChange={field.onChange}
              />
            );
          }}
        />
        {<Error>{errors?.accountName?.message}</Error>}
      </div>

      <div>
        <Controller
          name="senderIdNumber"
          control={control}
          rules={{ required: "Sender ID number is required" }}
          render={({ field }) => {
            return (
              <Input
                label={"Sender ID number"}
                value={field.value}
                onChange={field.onChange}
              />
            );
          }}
        />
        {<Error>{errors?.senderIdNumber?.message}</Error>}
      </div>
      <div>
        <Controller
          name="paymentDescription"
          control={control}
          rules={{ required: "Payment description is required" }}
          render={({ field }) => {
            return (
              <CustomSelect
                {...field}
                options={GHS_PAYMENT_DESCRIPTION}
                $width="100%"
                placeholder="Select payment description"
                showSearch
              />
            );
          }}
        />
        {<Error>{errors?.paymentDescription?.message}</Error>}
      </div>

      <ItemWrapper>
        <Button
          onClick={handlePrevious}
          label="Prev"
          height="48px"
          theme="alternate"
          width="203px"
        />

        <Button
          onClick={handleSubmit(onSubmit)}
          label="Continue"
          height="48px"
        />
      </ItemWrapper>
    </ContentWrapper>
  );
};

export default Beneficiary;
